import { Trans } from 'next-i18next';
import { Market, ProductCategoryCode, ProductType } from 'pleinchamp-api-client';
import React, { ComponentProps, FC, MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthModal } from '@auth/business/AuthModal.hook';
import { FavoriteButton } from '@components/buttons/FavoriteButton/FavoriteButton.component';
import { Text } from '@components/Text/Text.component';
import { useModal } from '@layout/business/Layout.utils';
import { MAX_PREFERENCES_PRODUCT_TYPES } from '@settings/business/settings.utils';
import { AppDispatch, RootState } from '@store/store';

type Props = Omit<ComponentProps<typeof FavoriteButton>, 'onClick' | 'isFavorite'> & {
  productType: ProductType;
  market?: Market;
};

const StockIsFavorite: FC<Props> = ({ productType, market, className, ...rest }) => {
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const { show } = useModal();
  useEffect(() => {
    if (isWarningModalOpen) {
      show({
        children: (
          <Text variant="p">
            <Trans i18nKey="stocks.home.max-favorites" ns="stocks">
              Vous avez atteint <br /> Vous devez en supprimer
            </Trans>
          </Text>
        ),
        close: () => setIsWarningModalOpen(false),
        visible: isWarningModalOpen,
      });
    }
  }, [isWarningModalOpen, setIsWarningModalOpen]);

  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const productTypes = useSelector((state: RootState) => state.settings.productTypes);

  const {
    pendingSettings: { setProductTypes },
    settings: { addProductType, removeUserProductTypes },
  } = useDispatch<AppDispatch>();
  const { showSignInModal } = useAuthModal();

  // TODO: add marketId check if in props => need api update
  const isFavorite = productTypes.some((pt) => {
    // Only Product category Cattle are based on market
    if (pt.productCategoryCode === ProductCategoryCode.Cattle && market) {
      return pt.id === productType.id && pt.marketReference?.id === market.id;
    }
    return pt.id === productType.id;
  });

  function handleSetFavorite(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (!isAuthenticated) {
      setProductTypes([{ ...productType, priority: 1 }]);
      showSignInModal();
    } else if (isFavorite) {
      removeUserProductTypes([{ id: productType.id, marketId: market?.id }]);
    } else if (productTypes.length < MAX_PREFERENCES_PRODUCT_TYPES) {
      addProductType({ id: productType.id, marketId: market?.id });
    } else {
      setIsWarningModalOpen(true);
    }
  }

  return <FavoriteButton className={className} isFavorite={isFavorite} onClick={handleSetFavorite} {...rest} />;
};

export { StockIsFavorite };
